import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { Eye } from "lucide-react";
import ToolManagmentCard from "../../../../../../common/element/Card/ToolManagmnetCard";
import AddEditQuestionAnswer from "./EditQuestionAnswer";
import { getSalesTrainingSessionDetails } from "../../../../../../redux/SalesTrainingCompanies/SalesTrainingCompaniesslice";
import {
  selectSalesTrainingSessionDetails,
  selectSalesTrainingSessionDetailsLoading,
} from "../../../../../../redux/SalesTrainingCompanies/SalesTrainingCompaniesselectors";
import ThreeDotBouncingLoader from "../../../../../../common/element/ButtonLoader/ThreeDotBouncingLoder";

const formatToolName = (name) => {
  if (!name) return "";
  return name.split("-").map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(" ");
};

const capitalizeFirstLetter = (str) => {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

const getStatusBadge = (status) => {
  switch (status?.toLowerCase()) {
    case "completed": return "text-green-600 bg-green-50";
    case "upcoming": return "text-blue-600 bg-blue-50";
    case "cancelled": return "text-red-600 bg-red-50";
    case "ongoing": return "text-[#CA8A04] bg-[#FEF9C3]";
    default: return "text-gray-600 bg-gray-50";
  }
};

const getFileNameFromUrl = (url) => {
  if (!url) return "Document";
  return url.substring(url.lastIndexOf("/") + 1);
};

const ToolResultSection = ({ tool }) => {
  const toolId = tool.tool_id;
  const score = tool.result_json;
  const isCompleted = tool.status === "completed";

  const isNCodesResult = toolId === 1 || toolId === 2 || toolId === 3;
  const isInnerDriveResult = toolId === 4;
  const isHumanNeedsResult = toolId === 8;
  const isBaselineResult = toolId === 5;
  const isPersonalityTestResult = toolId === 6;
  const isTraitsTestResult = toolId === 7;

  const innerDriveAssessmentRows = useMemo(() => {
    if (isInnerDriveResult && score?.user_ordered_items?.top_assessment) {
      return score.user_ordered_items.top_assessment.map((name) => ({ name, score: score[name] ?? 0 }));
    }
    return [];
  }, [isInnerDriveResult, score]);

  const innerDriveCoachRows = useMemo(() => {
    if (isInnerDriveResult && score?.user_ordered_items?.top_after_coach) {
      return score.user_ordered_items.top_after_coach.map((name) => ({ name, score: score[name] ?? 0 }));
    }
    return [];
  }, [isInnerDriveResult, score]);

  const innerDriveFallbackRows = useMemo(() => {
    if (isInnerDriveResult && score) {
      return [
        { key: "Certainty", value: score.Certainty || 0 },
        { key: "Variety", value: score.Variety || 0 },
        { key: "Significance", value: score.Significance || 0 },
        { key: "Love/Connection", value: score["Love/Connection"] || 0 },
        { key: "Growth", value: score.Growth || 0 },
        { key: "Contribution", value: score.Contribution || 0 },
      ].sort((a, b) => b.value - a.value);
    }
    return [];
  }, [isInnerDriveResult, score]);

  const humanNeedsRows = useMemo(() => {
    if (isHumanNeedsResult && score) {
      return [
        { key: "Certainty", value: score.Certainty || 0 },
        { key: "Variety", value: score.Variety || 0 },
        { key: "Significance", value: score.Significance || 0 },
        { key: "Love/Connection", value: score["Love/Connection"] || 0 },
        { key: "Growth", value: score.Growth || 0 },
        { key: "Contribution", value: score.Contribution || 0 },
      ].sort((a, b) => b.value - a.value);
    }
    return [];
  }, [isHumanNeedsResult, score]);

  const personalityTestRows = useMemo(() => {
    if (isPersonalityTestResult && score) {
      return Object.entries(score)
        .filter(([key]) => key !== 'user_ordered_items')
        .map(([key, value]) => ({ key, value }))
        .sort((a, b) => b.value - a.value);
    }
    return [];
  }, [isPersonalityTestResult, score]);

  const personalityAssessmentType = useMemo(() => {
    if (isPersonalityTestResult && score?.user_ordered_items?.assessment_type) {
      return score.user_ordered_items.assessment_type.map((name) => ({ name, score: score[name] ?? 0 }));
    }
    return [];
  }, [isPersonalityTestResult, score]);

  const personalityCoachType = useMemo(() => {
    if (isPersonalityTestResult && score?.user_ordered_items?.coach_type) {
      return score.user_ordered_items.coach_type.map((name) => ({ name, score: score[name] ?? 0 }));
    }
    return [];
  }, [isPersonalityTestResult, score]);

  const highestPersonalityScore = personalityTestRows.length > 0 ? personalityTestRows[0]?.value : 0;

  const traitsTestRows = useMemo(() => {
    if (isTraitsTestResult && score) {
      const traits = [];
      const traitNames = Object.keys(score).filter((key) => key.endsWith("Level")).map((key) => key.replace("Level", ""));
      traitNames.forEach((trait) => {
        traits.push({ name: trait.charAt(0).toUpperCase() + trait.slice(1), level: score[`${trait}Level`] || "", score: score[`${trait}Score`] || 0 });
      });
      return traits.sort((a, b) => b.score - a.score);
    }
    return [];
  }, [isTraitsTestResult, score]);

  if (!isCompleted) {
    return (
      <div>
        <h3 className="font-lato font-semibold text-[16px] leading-[150%] text-[#101828] mb-3 text-start">{formatToolName(tool.tool_name)}</h3>
        <p className="font-lato font-normal text-[14px] leading-[150%] text-center text-[#475467] mb-2">Not Completed Yet.</p>
      </div>
    );
  }

  return (
    <div className="bg-white overflow-hidden">
      {isNCodesResult && score?.selected_codes && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead><tr className="bg-[#FFF4E5]"><th className="py-3 px-4 font-lato font-medium text-sm text-[#475467] rounded-l-md">N-Code</th><th className="py-3 px-4 font-lato font-medium text-sm text-[#475467] rounded-r-md">E-Code</th></tr></thead>
            <tbody className="bg-white text-start">
              {score.selected_codes.map((item, index) => (
                <tr key={item.id || index} className="border-t border-gray-200">
                  <td className="py-3 px-4 font-lato font-medium text-sm text-[#1D2939]">{item.ncode}</td>
                  <td className="py-3 px-4 font-lato font-medium text-sm text-[#1D2939]">{item.ecode}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {isInnerDriveResult && (
        innerDriveAssessmentRows.length > 0 ? (
          <div className="space-y-6">
            <div>
              <h4 className="font-lato font-semibold text-[14px] leading-[150%] text-[#101828] mb-3 text-start">
                Top 2 Inner Drives – As Per Assessment:
              </h4>
              <table className="w-full border border-gray-200 rounded-lg overflow-hidden">
                <thead>
                  <tr className="bg-[#FFF4E5]">
                    <th className="py-3 px-4 text-left font-lato font-medium text-sm text-[#475467]">Rank</th>
                    <th className="py-3 px-4 text-left font-lato font-medium text-sm text-[#475467]">Inner Drive</th>
                    <th className="py-3 px-4 text-left font-lato font-medium text-sm text-[#475467]">Score</th>
                  </tr>
                </thead>
                <tbody className="bg-white">
                  {innerDriveAssessmentRows.map((item, index) => (
                    <tr key={item.name} className="border-t border-gray-200 text-start">
                      <td className="py-3 px-4 font-lato font-medium text-sm text-[#1D2939]">{index + 1}</td>
                      <td className="py-3 px-4 font-lato font-medium text-sm text-[#1D2939]">{item.name}</td>
                      <td className="py-3 px-4 font-lato font-medium text-sm text-[#1D2939]">{String(item.score).padStart(2, '0')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {innerDriveCoachRows.length > 0 && (
              <div>
                <h4 className="font-lato font-semibold text-[14px] leading-[150%] text-[#101828] mb-3 text-start">
                  Final Top 2 Inner Drives – After Discussion With Coach:
                </h4>
                <table className="w-full border border-gray-200 rounded-lg overflow-hidden">
                  <thead>
                    <tr className="bg-[#FFF4E5]">
                      <th className="py-3 px-4 text-left font-lato font-medium text-sm text-[#475467]">Rank</th>
                      <th className="py-3 px-4 text-left font-lato font-medium text-sm text-[#475467]">Inner Drive</th>
                      <th className="py-3 px-4 text-left font-lato font-medium text-sm text-[#475467]">Score</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white">
                    {innerDriveCoachRows.map((item, index) => (
                      <tr key={item.name} className="border-t border-gray-200 text-start">
                        <td className="py-3 px-4 font-lato font-medium text-sm text-[#1D2939]">{index + 1}</td>
                        <td className="py-3 px-4 font-lato font-medium text-sm text-[#1D2939]">{item.name}</td>
                        <td className="py-3 px-4 font-lato font-medium text-sm text-[#1D2939]">{String(item.score).padStart(2, '0')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="bg-[#FFF4E5]">
                <th className="py-3 px-4 text-left font-lato font-medium text-sm text-[#475467] w-16 rounded-l-md">S No.</th>
                <th className="py-3 px-4 text-left font-lato font-medium text-sm text-[#475467]">Personality Type</th>
                <th className="py-3 px-4 text-left font-lato font-medium text-sm text-[#475467] rounded-r-md">Score</th>
              </tr>
            </thead>
            <tbody className="bg-white text-start">
              {innerDriveFallbackRows.map((item, index) => (
                <tr key={item.key} className="border-t border-gray-200 hover:bg-gray-50">
                  <td className="py-3 px-4 font-lato font-medium text-sm text-[#1D2939]">{String(index + 1).padStart(2, '0')}</td>
                  <td className="py-3 px-4 font-lato font-medium text-sm text-[#1D2939]">{item.key}</td>
                  <td className="py-3 px-4 font-lato font-medium text-sm text-[#1D2939]">{item.value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      )}

      {isHumanNeedsResult && humanNeedsRows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead><tr className="bg-[#FFF4E5]"><th className="py-3 px-4 font-lato font-medium text-sm text-[#475467] w-16 rounded-l-md">S No.</th><th className="py-3 px-4 font-lato font-medium text-sm text-[#475467]">Personality Type</th><th className="py-3 px-4 font-lato font-medium text-sm text-[#475467] rounded-r-md">Score</th></tr></thead>
            <tbody className="bg-white text-start">
              {humanNeedsRows.map((item, index) => (
                <tr key={item.key} className="border-t border-gray-200 hover:bg-gray-50">
                  <td className="py-3 px-4 font-lato font-medium text-sm text-[#1D2939]">{String(index + 1).padStart(2, "0")}</td>
                  <td className="py-3 px-4 font-lato font-medium text-sm text-[#1D2939]">{item.key}</td>
                  <td className="py-3 px-4 font-lato font-medium text-sm text-[#1D2939]">{item.value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {isBaselineResult && (
        <div className="grid grid-cols-2 gap-6">
          {score?.selected_questions && (
            <div>
              <p className="font-lato font-semibold text-[14px] text-[#101828] mb-3 text-center">
                System Generated Baseline
              </p>
              <div className="flex flex-col gap-3">
                {score.selected_questions.map((item, index) => (
                  <div key={item.id} className="bg-[#FF8E1C] transition-colors duration-200 rounded-lg px-4 py-2 min-h-[42px] flex items-center justify-center text-white text-center font-medium text-sm shadow-sm">
                    {index + 1}. {item.text}
                  </div>
                ))}
              </div>
            </div>
          )}
          {score?.user_ordered_items && (
            <div>
              <p className="font-lato font-semibold text-[14px] text-[#101828] mb-3 text-center">
                User Reordered Baseline
              </p>
              <div className="flex flex-col gap-3">
                {score.user_ordered_items.map((item, index) => (
                  <div key={item.id} className="bg-[#FF8E1C] transition-colors duration-200 rounded-lg px-4 py-2 min-h-[42px] flex items-center justify-center text-white text-center font-medium text-sm shadow-sm">
                    {index + 1}. {item.text}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {isPersonalityTestResult && (
        <div className="space-y-4 p-1">
          {personalityAssessmentType.length === 0 && personalityTestRows.length > 0 && (
            <table className="w-full">
              <thead>
                <tr className="bg-[#FFF4E5]">
                  <th className="py-2 px-3 text-left font-lato font-medium text-sm text-[#475467]">S No.</th>
                  <th className="py-2 px-3 text-left font-lato font-medium text-sm text-[#475467]">Personality Type</th>
                  <th className="py-2 px-3 text-left font-lato font-medium text-sm text-[#475467]">Your Score</th>
                </tr>
              </thead>
              <tbody className="text-start">
                {personalityTestRows.map((item, index) => (
                  <tr key={index} className="border-t border-gray-100">
                    <td className={`py-2 px-3 font-lato text-sm ${item.value === highestPersonalityScore ? 'text-orange-600 font-semibold' : 'text-[#1D2939]'}`}>{String(index + 1).padStart(2, '0')}</td>
                    <td className={`py-2 px-3 font-lato text-sm ${item.value === highestPersonalityScore ? 'text-orange-600 font-semibold' : 'text-[#1D2939]'}`}>{item.key}</td>
                    <td className={`py-2 px-3 font-lato text-sm ${item.value === highestPersonalityScore ? 'text-orange-600' : 'text-[#1D2939]'}`}>{item.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {personalityAssessmentType.length > 0 && (
            <div>
              <h4 className="font-lato font-semibold text-[13px] leading-[150%] text-[#101828] mb-2 text-start">
                Personality Type – As Per Assessment:
              </h4>
              <table className="w-full border border-gray-200 rounded-lg overflow-hidden">
                <thead>
                  <tr className="bg-[#FFF4E5]">
                    <th className="py-2 px-3 text-left font-lato font-medium text-sm text-[#475467]">Rank</th>
                    <th className="py-2 px-3 text-left font-lato font-medium text-sm text-[#475467]">Personality Type</th>
                    <th className="py-2 px-3 text-left font-lato font-medium text-sm text-[#475467]">Score</th>
                  </tr>
                </thead>
                <tbody className="text-start">
                  {personalityAssessmentType.map((item, index) => (
                    <tr key={index} className="border-t border-gray-100">
                      <td className="py-2 px-3 font-lato text-sm text-[#1D2939]">{index + 1}</td>
                      <td className="py-2 px-3 font-lato text-sm text-[#1D2939]">{item.name}</td>
                      <td className="py-2 px-3 font-lato text-sm text-[#1D2939]">{String(item.score).padStart(2, '0')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {personalityCoachType.length > 0 && (
            <div>
              <h4 className="font-lato font-semibold text-[13px] leading-[150%] text-[#101828] mb-2 text-start">
                Personality Type – After Discussion With Coach:
              </h4>
              <table className="w-full border border-gray-200 rounded-lg overflow-hidden">
                <thead>
                  <tr className="bg-[#FFF4E5]">
                    <th className="py-2 px-3 text-left font-lato font-medium text-sm text-[#475467]">Rank</th>
                    <th className="py-2 px-3 text-left font-lato font-medium text-sm text-[#475467]">Personality Type</th>
                    <th className="py-2 px-3 text-left font-lato font-medium text-sm text-[#475467]">Score</th>
                  </tr>
                </thead>
                <tbody className="text-start">
                  {personalityCoachType.map((item, index) => (
                    <tr key={index} className="border-t border-gray-100">
                      <td className="py-2 px-3 font-lato text-sm text-[#1D2939]">{index + 1}</td>
                      <td className="py-2 px-3 font-lato text-sm text-[#1D2939]">{item.name}</td>
                      <td className="py-2 px-3 font-lato text-sm text-[#1D2939]">{String(item.score).padStart(2, '0')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {isTraitsTestResult && traitsTestRows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead><tr className="bg-[#FFF4E5]"><th className="py-3 px-4 font-lato font-medium text-sm text-[#475467] rounded-l-md">S No.</th><th className="py-3 px-4 font-lato font-medium text-sm text-[#475467]">Trait</th><th className="py-3 px-4 font-lato font-medium text-sm text-[#475467]">Level</th><th className="py-3 px-4 font-lato font-medium text-sm text-[#475467] rounded-r-md">Score</th></tr></thead>
            <tbody className="bg-white text-start">
              {traitsTestRows.map((item, index) => (
                <tr key={index} className="border-t border-gray-200 hover:bg-gray-50">
                  <td className="py-3 px-4 font-lato font-medium text-sm text-[#1D2939]">{String(index + 1).padStart(2, "0")}</td>
                  <td className="py-3 px-4 font-lato font-medium text-sm text-[#1D2939]">{item.name}</td>
                  <td className="py-3 px-4 font-lato font-medium text-sm text-[#1D2939]">{capitalizeFirstLetter(item.level)}</td>
                  <td className="py-3 px-4 font-lato font-medium text-sm text-[#1D2939]">{item.score}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const SalesTrainingSessionView = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalQuestions, setModalQuestions] = useState([]);
  const [modalAssignment, setModalAssignment] = useState(null);
  const dispatch = useDispatch();
  const location = useLocation();
  const loading = useSelector(selectSalesTrainingSessionDetailsLoading);
  const sessionDetails = useSelector(selectSalesTrainingSessionDetails);

  const query = new URLSearchParams(location.search);
  const sales_training_company_id = query.get("id");
  const sales_training_company_session_id = query.get("session_id");

  useEffect(() => {
    if (sales_training_company_id && sales_training_company_session_id) {
      dispatch(getSalesTrainingSessionDetails({ sales_training_company_session_id, sales_training_company_id }));
    }
  }, [dispatch, sales_training_company_id, sales_training_company_session_id]);

  const handleOpenModal = (assignment) => {
    const questions = (assignment.questions || []).map((q, idx) => ({
      question_text: q.question_text || q.question || `Q${idx + 1}`,
      question_id: q.question_id || idx,
      question_type: q.question_type || "simple",
      sub_questions: q.sub_questions || [],
    }));
    setModalQuestions(questions);
    setModalAssignment(assignment);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => { setIsModalOpen(false); setModalQuestions([]); setModalAssignment(null); };

  const participant = sessionDetails?.participant;
  const employeeInfo = participant ? {
    name: [participant.first_name, participant.last_name].filter(Boolean).join(" ") || "-",
    mobile: participant.phone_number || "-",
    email: participant.email || "-",
  } : null;

  const companyInfo = participant ? { name: participant.company_name || "-", phone: participant.phone_landline || "-", address: participant.address || "-" } : null;

  const sessionInfo = {
    name: sessionDetails?.session_name || sessionDetails?.session_title || "-",
    date: sessionDetails?.session_date ? new Date(sessionDetails.session_date).toLocaleDateString() : "-",
    coach: sessionDetails?.coach ? `${sessionDetails.coach.first_name} ${sessionDetails.coach.last_name}` : "-",
  };

  const documents = sessionDetails?.supported_documents || sessionDetails?.supportedDocuments || [];
  const assignments = sessionDetails?.assignments || [];
  const tools = sessionDetails?.tools || [];

  const getFileIcon = (fileName) => {
    const ext = fileName?.toLowerCase() || "";
    const isImage = [".png", ".jpg", ".jpeg", ".gif", ".webp"].some((e) => ext.includes(e));
    const isPdf = ext.includes(".pdf");
    if (isImage) return <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M9.46602 12.666H3.28693C2.88305 12.666 2.68111 12.666 2.5876 12.5862C2.50647 12.5169 2.46341 12.4129 2.47178 12.3065C2.48143 12.1839 2.62422 12.0411 2.90981 11.7556L8.57844 6.08693C8.84244 5.82292 8.97445 5.69091 9.12667 5.64145C9.26057 5.59795 9.4048 5.59795 9.53869 5.64145C9.69091 5.69091 9.82292 5.82292 10.0869 6.08693L12.666 8.66602V9.46602M9.46602 12.666C10.5861 12.666 11.1462 12.666 11.574 12.448C11.9503 12.2563 12.2563 11.9503 12.448 11.574C12.666 11.1462 12.666 10.5861 12.666 9.46602M9.46602 12.666H3.86602C2.74591 12.666 2.18586 12.666 1.75803 12.448C1.38171 12.2563 1.07575 11.9503 0.884003 11.574C0.666016 11.1462 0.666016 10.5861 0.666016 9.46602V3.86602C0.666016 2.74591 0.666016 2.18586 0.884003 1.75803C1.07575 1.38171 1.38171 1.07575 1.75803 0.884003C2.18586 0.666016 2.74591 0.666016 3.86602 0.666016H9.46602C10.5861 0.666016 11.1462 0.666016 11.574 0.884003C11.9503 1.07575 12.2563 1.38171 12.448 1.75803C12.666 2.18586 12.666 2.74591 12.666 3.86602V9.46602M5.66602 4.33268C5.66602 5.06906 5.06906 5.66602 4.33268 5.66602C3.5963 5.66602 2.99935 5.06906 2.99935 4.33268C2.99935 3.5963 3.5963 2.99935 4.33268 2.99935C5.06906 2.99935 5.66602 3.5963 5.66602 4.33268Z" stroke="white" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" /></svg>;
    if (isPdf) return <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M13.4481 14.0911C13.4481 14.8975 12.7361 14.9472 12.5925 14.9488H3.40675C2.60188 14.9488 2.55341 14.2332 2.55188 14.0911L2.55177 1.90866C2.55177 1.10146 3.26505 1.05252 3.40664 1.0509H9.61178L9.61508 1.05418V3.47149C9.61508 3.95661 9.90888 4.87509 11.0215 4.87509H13.4274L13.448 4.89567L13.4481 14.0911ZM14 14.0911V4.66839L10.1675 0.826633V0.808759H10.1492L9.84138 0.5H3.40677C2.92017 0.5 2 0.794501 2 1.90892V14.0913C2 14.5785 2.29412 15.5 3.40677 15.5H12.5933C13.0798 15.4999 14 15.2054 14 14.0911Z" fill="white" /></svg>;
    return <svg width="14" height="16" viewBox="0 0 14 16" fill="none"><path fillRule="evenodd" clipRule="evenodd" d="M5.15783 0C4.96241 0 4.77499 0.0766254 4.63681 0.213011L0.215811 4.57665C0.0776328 4.71304 0 4.89802 0 5.09091V13.8182C0 15.0232 0.989677 16 2.2105 16H11.0525C12.2733 16 13.263 15.0232 13.263 13.8182V2.18182C13.263 0.976836 12.2733 0 11.0525 0H5.15783Z" fill="white" /></svg>;
  };

  const CardLoader = () => <div className="flex items-center justify-center py-8"><ThreeDotBouncingLoader /></div>;
  const InfoField = ({ label, value }) => (
    <div className="flex flex-col text-start">
      <span className="block font-lato font-medium text-[14px] text-[#98A2B3]">{label}</span>
      <span className="font-lato font-medium text-[16px] leading-[150%] text-[#101828] mb-0 text-start">{value}</span>
    </div>
  );

  return (
    <div>
      <div className="flex items-center justify-between">
        <h1 className="font-lato font-semibold text-[28px] leading-[38px] text-[#101828]">
          {loading ? "Session Details" : sessionInfo.name || "Session Details"}
        </h1>
        {!loading && (
          <span className={`px-4 py-1.5 text-sm font-medium rounded-full ${getStatusBadge(sessionDetails?.status)}`}>
            {sessionDetails?.status ? sessionDetails.status.charAt(0).toUpperCase() + sessionDetails.status.slice(1) : "-"}
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-4">
        <ToolManagmentCard>
          <div className="border-b border-gray-100 pb-4 mb-4 text-start"><h2 className="font-lato font-semibold text-[16px] text-[#101828]">Employee Information</h2></div>
          {loading ? <CardLoader /> : employeeInfo ? (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <InfoField label="Name" value={employeeInfo.name} />
                <InfoField label="Company Name" value={companyInfo?.name} />
                <InfoField label="Mobile Number" value={employeeInfo.mobile} />
                <InfoField label="Email" value={employeeInfo.email} />
                <InfoField label="Address" value={companyInfo?.address} />
              </div>
            </div>
          ) : <span className="text-gray-400 text-sm">No employee information available</span>}
        </ToolManagmentCard>

        <ToolManagmentCard>
          <div className="border-b border-gray-100 pb-4 mb-4 text-start"><h2 className="font-lato font-semibold text-[16px] text-[#101828]">Session Information</h2></div>
          {loading ? <CardLoader /> : (
            <div className="grid grid-cols-2 gap-y-6 gap-x-4">
              <InfoField label="Session Name" value={sessionInfo.name} />
              <InfoField label="Session Date" value={sessionInfo.date} />
              <InfoField label="Coach Name" value={sessionInfo.coach} />
            </div>
          )}
        </ToolManagmentCard>

        <ToolManagmentCard>
          <div className="border-b border-gray-100 pb-4 mb-4 text-start"><h2 className="font-lato font-semibold text-[16px] text-[#101828]">Supported Documents</h2></div>
          {loading ? <CardLoader /> : documents.length === 0 ? <span className="text-gray-400 text-sm">No documents available</span> : (
            <div className="flex flex-col gap-5">
              {documents.map((group, groupIndex) => (
                <div key={groupIndex} className="flex flex-col gap-2">
                  <h3 className="font-lato font-medium text-[14px] text-[#101828] text-start">{group.name}</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {group.documents?.map((doc, docIndex) => {
                      const fileName = getFileNameFromUrl(doc.url);
                      return (
                        <div key={docIndex} onClick={() => { if (doc.url) window.open(doc.url, "_blank", "noopener,noreferrer"); }} className="flex items-center gap-3 p-2 border border-gray-200 rounded-lg hover:border-[#FF8E1C] hover:bg-orange-50 transition-all cursor-pointer bg-white">
                          <div className="relative w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0">
                            <div className="absolute inset-0 rounded-full bg-[#FF8E1C] opacity-20" />
                            <div className="relative w-8 h-8 rounded-full bg-[#FF8E1C] flex items-center justify-center text-white">{getFileIcon(fileName)}</div>
                          </div>
                          <span className="flex-1 text-[13px] text-[#475467] truncate text-start" title={fileName}>{fileName}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </ToolManagmentCard>

        <ToolManagmentCard>
          <div className="pb-4 text-start"><h2 className="font-lato font-semibold text-[16px] text-[#101828]">Assignments</h2></div>
          {loading ? <CardLoader /> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead className="bg-[#FFF4E5] text-[#475467]">
                  <tr><th className="py-3 px-4 font-medium w-16 rounded-l-md">S No.</th><th className="py-3 px-4 font-medium">Assignment Name</th><th className="py-3 px-4 font-medium text-center rounded-r-md">Action</th></tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {assignments.length === 0 ? (
                    <tr><td colSpan={3} className="text-center text-gray-400 py-4">No assignments</td></tr>
                  ) : (
                    assignments.map((item, index) => (
                      <tr key={index}>
                        <td className="py-4 px-4 text-[#1D2939]">{`0${index + 1}`}</td>
                        <td className="py-4 px-4 text-[#1D2939]">{item.assignment_name || item.name || "-"}</td>
                        <td className="py-4 px-4 text-center">
                          <button onClick={() => handleOpenModal(item)} className="text-gray-500 hover:text-gray-700"><Eye size={18} /></button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </ToolManagmentCard>

        <ToolManagmentCard>
          <div className="pb-4 text-start"><h2 className="font-lato font-semibold text-[16px] text-[#101828]">Assessment Result</h2></div>
          <div className="border-b border-gray-200 mb-3"></div>
          {loading ? <CardLoader /> : tools.length === 0 ? (
            <div className="flex items-center justify-center py-8"><p className="font-lato font-normal text-[14px] text-[#475467]">No assessment data</p></div>
          ) : (
            <div className="space-y-6">
              {tools.map((tool, idx) => <ToolResultSection key={tool.tool_id || idx} tool={tool} />)}
            </div>
          )}
        </ToolManagmentCard>
      </div>

      <AddEditQuestionAnswer
        dialogOpen={isModalOpen}
        onDialogClose={handleCloseModal}
        questionsData={modalQuestions}
        isViewMode={true}
        sessionData={modalAssignment}
      />
    </div>
  );
};

export default SalesTrainingSessionView;
